/**
 * RightPanelContext - Contexto para gestionar el RightPanel
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/layouts/panels
 *
 * @description
 * Contexto y provider para coordinar la apertura/cierre del RightPanel
 * y gestionar cambios sin guardar.
 *
 * @remarks
 * **Características**:
 * - Registra el panel activo
 * - Gestiona función de validación de cambios sin guardar
 * - Coordina el cierre seguro del panel
 * - Previene pérdida de datos no guardados
 *
 * @example
 * ```tsx
 * function MyPanel({ onClose }) {
 *   const { registerUnsavedChangesCheck } = useRightPanel();
 *   const [formData, setFormData] = useState(initialData);
 *   const { hasUnsavedChanges, confirmDiscard } = useUnsavedChanges(initialData, formData);
 *
 *   useEffect(() => {
 *     registerUnsavedChangesCheck(confirmDiscard);
 *   }, [hasUnsavedChanges, confirmDiscard, registerUnsavedChangesCheck]);
 *
 *   return <div>...</div>;
 * }
 * ```
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Función que verifica cambios sin guardar
 * @returns true si se puede cerrar, false si el usuario canceló
 */
type UnsavedChangesCheckFn = () => Promise<boolean>;

/**
 * Tipo del panel activo
 */
type ActivePanelType =
  | 'tree'
  | 'grid-config'
  | 'grid-cells'
  | 'base-roads'
  | 'network-style'
  | 'network-node'
  | 'network-edge'
  | 'network-analysis'
  | 'building'
  | 'urban-entity'
  | null;

/**
 * Contexto del RightPanel
 */
interface RightPanelContextValue {
  /**
   * Panel actualmente activo
   */
  activePanel: ActivePanelType;

  /**
   * Establece el panel activo
   */
  setActivePanel: (panel: ActivePanelType) => void;

  /**
   * Registra una función para verificar cambios sin guardar
   */
  registerUnsavedChangesCheck: (checkFn: UnsavedChangesCheckFn | null) => void;

  /**
   * Intenta cerrar el panel de forma segura
   * @returns true si se cerró, false si el usuario canceló
   */
  safeClose: () => Promise<boolean>;

  /**
   * Verifica si se puede cambiar el contenido del panel sin cerrarlo
   * @returns true si se puede cambiar, false si el usuario canceló
   */
  canChangeContent: () => Promise<boolean>;
}

const RightPanelContext = createContext<RightPanelContextValue | undefined>(undefined);

/**
 * Props del provider
 */
interface RightPanelProviderProps {
  children: ReactNode;
}

/**
 * Provider del RightPanel
 */
export function RightPanelProvider({ children }: RightPanelProviderProps) {
  const [activePanel, setActivePanelState] = useState<ActivePanelType>(null);
  const [unsavedChangesCheck, setUnsavedChangesCheck] = useState<UnsavedChangesCheckFn | null>(null);

  /**
   * Wrapper de setActivePanel con logs
   */
  const setActivePanel = useCallback((panel: ActivePanelType) => {
    console.log('[RightPanelContext] setActivePanel llamado:', panel);
    console.log('[RightPanelContext]   Panel actual:', activePanel);
    setActivePanelState(panel);
  }, [activePanel]);

  /**
   * Registra la función de verificación de cambios sin guardar
   */
  const registerUnsavedChangesCheck = useCallback((checkFn: UnsavedChangesCheckFn | null) => {
    console.log('[RightPanelContext] registerUnsavedChangesCheck llamado:', !!checkFn);
    setUnsavedChangesCheck(() => checkFn);
  }, []);

  /**
   * Verifica si se puede cambiar el contenido del panel (sin cerrarlo)
   */
  const canChangeContent = useCallback(async (): Promise<boolean> => {
    console.log('[RightPanelContext] canChangeContent() llamado, unsavedChangesCheck:', !!unsavedChangesCheck);

    // Si hay una función de verificación registrada, ejecutarla
    if (unsavedChangesCheck) {
      console.log('[RightPanelContext] Ejecutando verificación de cambios sin guardar...');
      const canChange = await unsavedChangesCheck();
      console.log('[RightPanelContext] Resultado de verificación:', canChange);
      if (!canChange) {
        console.log('[RightPanelContext] Usuario canceló el cambio de contenido');
        return false; // Usuario canceló
      }
      console.log('[RightPanelContext] ✓ Usuario confirmó descarte de cambios');
    } else {
      console.log('[RightPanelContext] No hay función de verificación registrada, permitiendo cambio');
    }

    // NO limpiar el activePanel ni unsavedChangesCheck - solo estamos cambiando contenido
    return true;
  }, [unsavedChangesCheck]);

  /**
   * Intenta cerrar el panel de forma segura
   */
  const safeClose = useCallback(async (): Promise<boolean> => {
    console.log('[RightPanelContext] safeClose() llamado, unsavedChangesCheck:', !!unsavedChangesCheck);

    // Si hay una función de verificación registrada, ejecutarla
    if (unsavedChangesCheck) {
      console.log('[RightPanelContext] Ejecutando verificación de cambios sin guardar...');
      const canClose = await unsavedChangesCheck();
      console.log('[RightPanelContext] Resultado de verificación:', canClose);
      if (!canClose) {
        console.log('[RightPanelContext] Usuario canceló el cierre');
        return false; // Usuario canceló
      }
    } else {
      console.log('[RightPanelContext] No hay función de verificación registrada, permitiendo cierre');
    }

    // Limpiar estado
    setActivePanelState(null);
    setUnsavedChangesCheck(null);
    console.log('[RightPanelContext] Panel cerrado exitosamente');
    return true;
  }, [unsavedChangesCheck]);

  const value: RightPanelContextValue = {
    activePanel,
    setActivePanel,
    registerUnsavedChangesCheck,
    safeClose,
    canChangeContent,
  };

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
}

/**
 * Hook para usar el contexto del RightPanel
 */
export function useRightPanel(): RightPanelContextValue {
  const context = useContext(RightPanelContext);
  if (!context) {
    throw new Error('useRightPanel debe usarse dentro de RightPanelProvider');
  }
  return context;
}

/**
 * Hook "safe" para usar el contexto del RightPanel
 *
 * @returns Contexto del RightPanel o null si no está disponible
 *
 * @remarks
 * Usar este hook cuando el componente puede existir sin RightPanelProvider
 * (ej: MapControls usado en ProjectForm sin infraestructura completa de MapLayout)
 */
export function useRightPanelSafe(): RightPanelContextValue | null {
  return useContext(RightPanelContext) ?? null;
}
